import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Sparkles, ListChecks } from "lucide-react";
import { StudentShell } from "../components/StudentShell";
import { SheetSolver } from "../components/Worksheet";
import { useLang } from "../i18n";
import { api, useAuth } from "../api";
import { gradeProfile } from "../gradeProfile";
import { sfx } from "../sfx";

export default function Quiz() {
  const { t } = useLang();
  const { user } = useAuth();
  const profile = gradeProfile(user?.grade);
  const [kinds, setKinds] = useState([]);
  const [left, setLeft] = useState(null);
  const [cfg, setCfg] = useState({ kind: "", count: profile.quiz.count });
  const [quiz, setQuiz] = useState(null);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wordLog, setWordLog] = useState({ wrong: [], right: [] });
  const [sheet, setSheet] = useState(null);
  const [mixedResult, setMixedResult] = useState(null);

  const buildMixed = async () => {
    setBusy("mixed");
    try {
      const { data } = await api.post("/practice/mixed", {});
      setSheet(data); setMixedResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بناء الورقة");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    api.get("/ai/quiz/kinds").then((r) => {
      setKinds(r.data.kinds);
      setCfg((c) => ({ ...c, kind: r.data.kinds[0]?.id || "" }));
    });
    api.get("/ai/limits").then((r) => setLeft(r.data.quiz)).catch(() => {});
  }, []);

  const start = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post("/ai/quiz/graded", { kind, count: cfg.count });
      if (!data.questions?.length) throw new Error("empty");
      setQuiz(data); setI(0); setScore(0); setDone(false); setPicked(null);
      setWordLog({ wrong: [], right: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر توليد الكويز، حاول مرة أخرى");
    } finally { setBusy(false); }
  };

  const choose = (k) => {
    if (picked !== null) return;
    setPicked(k);
    const q = quiz.questions[i];
    const correct = k === q.answer;
    const word = q.word || "";
    if (word) setWordLog((w) => (correct
      ? { ...w, right: [...w.right, word] } : { ...w, wrong: [...w.wrong, word] }));
    if (correct) { setScore((s) => s + 1); sfx.correct(); toast.success(t("correct")); }
    else { sfx.wrong(); toast.error(t("wrong")); }
  };

  const next = async () => {
    if (i + 1 < quiz.questions.length) { setI(i + 1); setPicked(null); return; }
    setDone(true);
    sfx.points();
    await api.post("/ai/quiz/result", {
      topic: quiz.topic, score, total: quiz.questions.length,
      wrong_words: wordLog.wrong, right_words: wordLog.right,
    });
  };

  if (sheet) return (
    <StudentShell>
      <h1 className="text-3xl font-black text-white glow-text">ورقة مخلوطة</h1>
      <div className="play-card mt-6 max-w-2xl" data-testid="quiz-mixed-sheet">
        {mixedResult ? (
          <div className="space-y-3" data-testid="mixed-result">
            <p className="text-2xl font-black"
              style={{ color: mixedResult.auto_correct >= mixedResult.auto_total / 2 ? "#3ec46d" : "#ff8080" }}>
              {mixedResult.auto_correct} من {mixedResult.auto_total} صحيحة</p>
            {!!mixedResult.pending_manual && (
              <p className="text-sm text-white/70">
                {mixedResult.pending_manual} سؤال مفتوح — راجعه مع معلمك</p>
            )}
            <button onClick={() => { setSheet(null); setMixedResult(null); }}
              data-testid="mixed-close-btn" className="play-btn text-white w-full"
              style={{ background: "var(--play-primary)" }}>رجوع للكويزات</button>
          </div>
        ) : (
          <>
            {!!sheet.weak_words_used?.length && (
              <p className="text-xs mb-3" style={{ color: "var(--play-accent)" }}
                data-testid="mixed-weak-used">
                كلماتك الصعبة في هذه الورقة: {sheet.weak_words_used.join(" · ")}</p>
            )}
            <SheetSolver sheet={sheet} busy={busy === "check"} testid="mixed-solver"
              onSubmit={async (answers) => {
                setBusy("check");
                try {
                  const { data } = await api.post(`/practice/mixed/${sheet.id}/check`, { answers });
                  setMixedResult(data); sfx.points();
                } catch { toast.error("تعذّر التصحيح"); } finally { setBusy(false); }
              }} />
          </>
        )}
      </div>
    </StudentShell>
  );

  if (!quiz) return (
    <StudentShell>      <h1 className="text-4xl font-black text-white glow-text">{t("quiz")}</h1>
      <p className="text-white/70 mt-2 text-sm">
        اختر نوع الأسئلة المناسب للصف {user?.grade} — الأسئلة تُبنى تلقائياً من كلمات منهجك.</p>
      {left?.cap > 0 && (
        <p className="text-xs mt-2 font-black" style={{ color: "var(--play-accent)" }}
          data-testid="quiz-daily-left">
          كويزاتك المتبقية اليوم: {left.left} من {left.cap}</p>
      )}

      <div className="flex items-end gap-3 mt-6">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-white/65">{t("questions")}</span>
          <select className="input mt-1 w-24" data-testid="quiz-count-select" value={cfg.count}
            onChange={(e) => setCfg({ ...cfg, count: Number(e.target.value) })}>
            {profile.quiz.options.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-5" data-testid="quiz-kinds">
        {kinds.map((k, idx) => (
          <motion.button key={k.id} data-testid={`quiz-kind-${k.id}`} onClick={() => start(k.id)}
            disabled={!!busy} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.07 }} whileHover={{ y: -5 }}
            className="play-card text-start disabled:opacity-60">
            <div className="text-4xl">{k.emoji}</div>
            <div className="font-black mt-3">{k.label_ar}</div>
            <div className="text-xs text-white/60 mt-1">{k.desc_ar}</div>
            <div className="mt-4 text-xs font-black flex items-center gap-1"
              style={{ color: "var(--play-accent)" }}>
              {busy === k.id ? <><Loader2 className="animate-spin" size={14} />جارٍ التوليد...</>
                : <><Sparkles size={14} />{t("startQuiz")}</>}
            </div>
          </motion.button>
        ))}
        <motion.button data-testid="quiz-kind-mixed" onClick={buildMixed} disabled={!!busy}
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: kinds.length * 0.07 }} whileHover={{ y: -5 }}
          className="play-card text-start disabled:opacity-60"
          style={{ borderColor: "var(--play-primary)" }}>
          <div className="text-4xl">🧾</div>
          <div className="font-black mt-3">ورقة مخلوطة</div>
          <div className="text-xs text-white/60 mt-1">
            أنواع مختلفة في ورقة واحدة، من كلماتك الصعبة</div>
          <div className="mt-4 text-xs font-black flex items-center gap-1"
            style={{ color: "var(--play-accent)" }}>
            {busy === "mixed" ? <><Loader2 className="animate-spin" size={14} />جارٍ التوليد...</>
              : <><ListChecks size={14} />ابدأ الورقة</>}
          </div>
        </motion.button>
        {!kinds.length && <p className="text-white/50 text-sm" data-testid="quiz-kinds-empty">...جارٍ التحميل</p>}
      </div>
    </StudentShell>
  );

  if (done) return (
    <StudentShell>
      <div className="play-card max-w-lg mx-auto text-center mt-12" data-testid="quiz-result"
        style={{ borderColor: "var(--play-primary)" }}>
        <div className="text-6xl">🎉</div>
        <h2 className="text-3xl font-black mt-4">{t("yourScore")}</h2>
        <div className="text-6xl font-black mt-2" style={{ color: "var(--play-primary)" }}>
          {score}/{quiz.questions.length}</div>
        <button onClick={() => setQuiz(null)} data-testid="quiz-again-btn"
          className="play-btn text-white mt-8" style={{ background: "var(--play-secondary)" }}>{t("tryAgain")}</button>
      </div>
    </StudentShell>
  );

  const q = quiz.questions[i];
  return (
    <StudentShell>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <span className="font-bold text-white/65">{i + 1} / {quiz.questions.length}</span>
          {quiz.focus && (
            <span className="text-xs font-black" style={{ color: "var(--play-accent)" }}
              data-testid="quiz-ladder">
              سلّم القواعد {quiz.step}/{quiz.ladder_total} · {quiz.focus}</span>
          )}
          <span className="font-black" style={{ color: "var(--play-primary)" }}>⭐ {score}</span>
        </div>
        <motion.div key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          className="play-card mt-4" data-testid="quiz-question">
          {quiz.passage && (
            <div className="rounded-2xl p-4 mb-5 text-sm leading-loose" dir="ltr"
              style={{ background: "rgba(255,255,255,.07)" }} data-testid="quiz-passage">
              {quiz.passage}</div>
          )}
          <h2 className="text-2xl font-black leading-snug" dir="ltr">{q.question}</h2>
          <div className="space-y-3 mt-6">
            {(q.options || []).map((o, k) => {
              const isRight = k === q.answer, chosen = picked === k;
              const bg = picked === null ? "rgba(255,255,255,.1)" : isRight ? "#2fa84f" : chosen ? "#d92b45" : "rgba(255,255,255,.1)";
              const color = "#fff";
              return (
                <button key={k} onClick={() => choose(k)} data-testid={`quiz-option-${k}`}
                  dir={/[a-zA-Z]/.test(String(o)) ? "ltr" : "rtl"}
                  className="w-full text-start rounded-2xl border-2 px-5 py-4 font-bold transition-colors"
                  style={{ background: bg, color, borderColor: "rgba(255,255,255,.2)" }}>{o}</button>
              );
            })}
          </div>
          {picked !== null && (
            <>
              <p className="mt-4 text-sm text-white/75" data-testid="quiz-explanation">{q.explanation_ar}</p>
              <button onClick={next} data-testid="quiz-next-btn"
                className="play-btn text-white w-full mt-4" style={{ background: "var(--play-primary)" }}>
                {i + 1 === quiz.questions.length ? t("finish") : t("next")}</button>
            </>
          )}
        </motion.div>
      </div>
    </StudentShell>
  );
}
